<script setup>
import { computed } from 'vue';
import ChatIcon from './ChatIcon.vue';
import ChatAvatar from './ChatAvatar.vue';

const props = defineProps({
  theme: { type: Object, required: true },
  subtitle: { type: String, default: '' },
  whatsappUrl: { type: String, default: '' },
  closable: { type: Boolean, default: true },
  t: { type: Function, required: true },
});

defineEmits(['close']);

const surfaceClass = computed(
  () =>
    ({
      gradient: 'bg-gradient-to-br from-primary to-accent text-on-primary',
      light: 'bg-surface text-ink border-b border-line',
    })[props.theme.header_style] || 'bg-primary text-on-primary'
);

const isLight = computed(() => props.theme.header_style === 'light');
const actionClass = computed(() =>
  isLight.value
    ? 'text-muted hover:bg-ink/5 hover:text-ink focus-visible:ring-primary/40'
    : 'text-current opacity-85 hover:bg-on-primary/15 hover:opacity-100 focus-visible:ring-on-primary/50'
);
</script>

<template>
  <header class="relative shrink-0" :class="surfaceClass">
    <div
      class="flex items-center gap-3 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))]"
    >
      <span class="relative">
        <ChatAvatar
          :src="theme.avatar_url"
          :name="theme.title"
          size="md"
          :tone="isLight ? 'default' : 'contrast'"
          :class="isLight ? '' : 'ring-2 ring-on-primary/25'"
        />
        <span
          class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-whatsapp ring-2"
          :class="isLight ? 'ring-surface' : 'ring-primary'"
        />
      </span>
      <div class="min-w-0 flex-1">
        <h2
          class="truncate text-[15.5px] font-semibold leading-tight tracking-[-0.01em]"
        >
          {{ theme.title }}
        </h2>
        <p
          v-if="subtitle"
          class="mt-0.5 truncate text-[12.5px] leading-snug opacity-80"
        >
          {{ subtitle }}
        </p>
      </div>
      <a
        v-if="whatsappUrl"
        :href="whatsappUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex h-9 w-9 items-center justify-center rounded-full outline-none transition focus-visible:ring-2"
        :class="actionClass"
        :title="theme.whatsapp_label || t('WHATSAPP_DEFAULT')"
        :aria-label="theme.whatsapp_label || t('WHATSAPP_DEFAULT')"
      >
        <ChatIcon name="whatsapp" class="h-[18px] w-[18px]" />
      </a>
      <button
        v-if="closable"
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full outline-none transition focus-visible:ring-2"
        :class="actionClass"
        :aria-label="t('CLOSE')"
        @click="$emit('close')"
      >
        <ChatIcon name="chevron-down" class="h-6 w-6" />
      </button>
    </div>
    <!-- The accent band: a brand's second colour, kept to a single line so it never competes with content. -->
    <span
      v-if="theme.accent_color && theme.header_style === 'solid'"
      class="absolute inset-x-0 bottom-0 h-[3px] bg-accent"
    />
  </header>
</template>
